import { inject, reactive, markRaw } from "vue";
import Keycloak from "keycloak-js";
import { cleanAuthData, clearOrganizationCache, clearOrgRecoveryFlag, hasOrganizationClaim, markLogoutAndCleanup, RECOVERY_FLAG, saveLoginEmail, } from "@/shared/utils/authCleanup";
import { logger } from "@/infrastructure/services/LoggerService";
import { routes } from "@/router/index";
import { initializeSession, checkSessionValidity, clearSession, } from "@/shared/utils/sessionManager";
import api from "@/plugins/api";
import { createAuthBootstrapService, } from "@/services/auth-bootstrap";
let keycloakConfig;
if (import.meta.env.PROD) {
    // This is a keycloak configuration file format. Please verify when updating the keycloak-js version that it still works.
    keycloakConfig = import.meta.env.VITE_KEYCLOAK_CONFIG_FILE;
}
else {
    /** Keycloak configuration from environment variables */
    keycloakConfig = {
        url: import.meta.env.VITE_KEYCLOAK_AUTH_SERVER_URL,
        realm: import.meta.env.VITE_KEYCLOAK_REALM,
        clientId: import.meta.env.VITE_KEYCLOAK_RESSOURCE,
    };
}
/** Injection key for Keycloak authentication state */
export const KEYCLOAK_KEY = Symbol("KEYCLOAK_KEY");
/** Initialize Keycloak instance */
const keycloak = new Keycloak(keycloakConfig);
// Reactive state for authentication
const authState = reactive({
    isAuthenticated: false,
    isInitialized: false,
    keycloakInstance: markRaw(keycloak),
    login: async (options) => {
        logger.debug("Starting login process...");
        logger.debug("Current auth state:", {
            isInitialized: authState.isInitialized,
            isAuthenticated: authState.isAuthenticated,
            hasKeycloakLogin: !!keycloak.login,
            keycloakAuthenticated: keycloak.authenticated,
        });
        try {
            // Wait for Keycloak initialization if not ready
            if (!authState.isInitialized) {
                logger.debug("Waiting for Keycloak initialization to complete...");
                // Wait for initialization to complete with timeout
                let attempts = 0;
                const maxAttempts = 150; // 15 seconds max wait (150 * 100ms)
                while (!authState.isInitialized && attempts < maxAttempts) {
                    await new Promise((resolve) => setTimeout(resolve, 100));
                    attempts++;
                }
                if (!authState.isInitialized) {
                    throw new Error("Keycloak initialization timeout - failed to initialize within 15 seconds");
                }
                logger.debug("Keycloak initialization completed, proceeding with login");
            }
            // Ensure we have a valid login method
            if (!keycloak.login) {
                throw new Error("Keycloak login method not available after initialization");
            }
            // Get user's selected locale for Keycloak login page
            const userLocale = getUserLocale();
            // Determine organization to use: prioritize explicit parameter over stored value
            // This allows organization context from URL (e.g., /confirm/ClientA) to take precedence
            const organizationToUse = options?.organization || localStorage.getItem("organizationCache");
            // Build Keycloak login options
            const keycloakLoginOptions = {
                scope: organizationToUse ? `organization:${organizationToUse}` : getScope(),
                redirectUri: options?.redirectUri,
                locale: userLocale,
            };
            // Add organization as kc_idp_hint to skip organization selection
            // This tells Keycloak which organization to use directly
            if (organizationToUse) {
                keycloakLoginOptions.idpHint = organizationToUse;
                logger.debug("Using kc_idp_hint (organization):", organizationToUse);
                if (options?.organization) {
                    logger.debug("Organization from explicit parameter (redirect context)");
                }
                else {
                    logger.debug("Organization from localStorage cache");
                }
            }
            // OP-5119: when we are recovering from an org-less session, force a
            // fresh authentication so Keycloak's SSO short-circuit (which keeps
            // re-using the deleted-org session note) cannot bypass the
            // OrganizationAuthenticator.action() flow.
            if (sessionStorage.getItem(RECOVERY_FLAG)) {
                keycloakLoginOptions.prompt = "login";
                logger.debug("OP-5119 recovery: forcing prompt=login");
            }
            // Add loginHint if provided (Keycloak official parameter to pre-fill email)
            if (options?.loginHint) {
                keycloakLoginOptions.loginHint = options.loginHint;
                logger.debug("Using login_hint (email):", options.loginHint);
            }
            logger.debug("Calling keycloak.login() with scope:", keycloakLoginOptions.scope);
            logger.debug("Redirect URI:", options?.redirectUri);
            logger.debug("Locale for Keycloak:", userLocale);
            logger.debug("Login hint:", options?.loginHint || "none");
            logger.debug("IDP hint (organization):", organizationToUse || "none");
            await keycloak.login(keycloakLoginOptions);
            logger.debug("keycloak.login() call completed");
        }
        catch (error) {
            logger.debug("Login failed:", error);
            throw error;
        }
    },
    logout: async () => {
        logger.debug("Starting logout process...");
        // Use shared cleanup function
        markLogoutAndCleanup();
        // Clear session tracking data
        clearSession();
        // Reset auth state immediately
        authState.isAuthenticated = false;
        // Try to perform Keycloak server logout to clear SSO session
        // Use the original keycloak instance directly instead of the reactive proxy
        // to avoid issues with private member access
        if (keycloak && keycloak.authenticated) {
            try {
                logger.debug("Attempting Keycloak server logout...");
                await keycloak.logout({
                    redirectUri: window.location.origin + "/session-ended",
                });
                return; // If successful, Keycloak handles redirect
            }
            catch (logoutError) {
                logger.debug("Keycloak server logout failed, proceeding with local logout:", logoutError);
                // Try fallback logout with login URL
                try {
                    await keycloak.logout({
                        redirectUri: window.location.origin + "/session-ended",
                    });
                    return;
                }
                catch (secondLogoutError) {
                    logger.debug("Second logout attempt failed:", secondLogoutError);
                }
            }
        }
        // Final fallback: local redirect to logout page
        logger.debug("Performing local logout and redirect...");
        window.location.replace(window.location.origin + "/session-ended");
    },
    reinitialize: async () => {
        logger.debug("Manually reinitializing Keycloak...");
        // Clear force logout flag
        sessionStorage.removeItem("force-logout");
        // Reset state
        authState.isAuthenticated = false;
        authState.isInitialized = false;
        // Reinitialize
        await initializeKeycloak();
        logger.debug("Keycloak reinitialization completed");
    },
});
// Helper function to save organization from token
function saveOrganizationFromToken() {
    // Check if the organization exists in the Keycloak ID token
    // Use idTokenParsed which contains the decoded ID token with organization info
    const organizationObj = keycloak.idTokenParsed?.organization;
    if (!organizationObj) {
        logger.debug("No organization found in ID token");
        return;
    }
    // Organization structure in Keycloak: { "org-name": { "id": "uuid", ... } }
    if (typeof organizationObj === "object" && organizationObj !== null) {
        // Extract the first key which is the organization name/identifier
        const keyOrganization = Object.keys(organizationObj)[0];
        if (keyOrganization) {
            logger.debug("Organization found in ID token:", keyOrganization);
            // Optionally extract the organization ID from nested data
            const organizationData = organizationObj[keyOrganization];
            if (organizationData?.id) {
                logger.debug("Organization ID:", organizationData.id);
            }
            // Store the organization key/name (not the UUID) for IDP hint
            // This is what Keycloak expects in the kc_idp_hint parameter
            localStorage.setItem("organizationCache", keyOrganization);
            logger.debug("Organization name saved to cache:", keyOrganization);
        }
        else {
            logger.debug("Organization object exists but has no keys:", organizationObj);
        }
    }
    else if (typeof organizationObj === "string") {
        // If it's already a string, use it directly
        localStorage.setItem("organizationCache", organizationObj);
        logger.debug("Organization (string) saved to cache:", organizationObj);
    }
    else {
        logger.debug("Organization field has unexpected type:", typeof organizationObj);
    }
}
// Helper function to get scope with organization
function getScope() {
    // Read from localStorage to persist across Keycloak redirects
    const storedOrganization = localStorage.getItem("organizationCache");
    return storedOrganization ? `organization:${storedOrganization}` : "organization";
}
// Discard the org-less authenticated session produced by case (b) so the
// router guard triggers a forced login with the plain `organization` scope.
function discardOrgLessSession() {
    logger.debug("Silent SSO succeeded without an organization claim; discarding session (OP-5119)");
    clearOrganizationCache();
    clearInvalidTokens();
    authState.isAuthenticated = false;
    authState.isInitialized = true;
}
// Build the URL of the OP-5119 terminal page.
function noOrgTerminalUrl() {
    return window.location.origin + "/session-ended?reason=no-org";
}
// State-B action: a `prompt=login` retry STILL produced an org-less token
// (typically the 0-orgs case). Logging the user out of Keycloak's SSO
// session so the terminal page is sticky on refresh, then redirecting to
// /session-ended?reason=no-org.
function escalateOrgLessSession() {
    logger.debug("Recovery retry still produced an org-less token; escalating to logout (OP-5119)");
    markLogoutAndCleanup();
    authState.isAuthenticated = false;
    authState.isInitialized = true;
    const terminalUrl = noOrgTerminalUrl();
    if (keycloak && keycloak.authenticated) {
        keycloak.logout({ redirectUri: terminalUrl }).catch((logoutError) => {
            logger.debug("Keycloak logout during OP-5119 escalation failed:", logoutError);
            window.location.replace(terminalUrl);
        });
        return;
    }
    window.location.replace(terminalUrl);
}
// Dispatcher for an authenticated-but-org-less session (OP-5119 case b).
// First detection → State A (set flag, soft discard, router will retry with
// prompt=login). Second detection → State B (escalate via logout).
function handleOrgLessSession() {
    if (sessionStorage.getItem(RECOVERY_FLAG)) {
        escalateOrgLessSession();
        return;
    }
    sessionStorage.setItem(RECOVERY_FLAG, "1");
    discardOrgLessSession();
}
// Helper function to get user's selected locale for Keycloak
function getUserLocale() {
    // Priority: selectedLocale (manual selection) > locale (from store)
    const selectedLocale = localStorage.getItem("selectedLocale") || localStorage.getItem("locale");
    if (selectedLocale) {
        // Convert full locale code to simple language code for Keycloak
        // e.g., "fr-FR" -> "fr", "en-US" -> "en", "nl-NL" -> "nl"
        if (selectedLocale.startsWith("fr"))
            return "fr";
        if (selectedLocale.startsWith("nl"))
            return "nl";
        if (selectedLocale.startsWith("en"))
            return "en";
    }
    // Default to English if no locale found
    return "en";
}
// Helper function to check if a path matches a public page based on route metadata
function isPublicPage(pathname) {
    // Helper to match dynamic route patterns like /payment/:paymentId
    const matchesRoute = (routePath, pathname) => {
        // Convert route pattern to regex (e.g., /payment/:paymentId -> /payment/[^/]+)
        const pattern = routePath.replace(/:\w+/g, "[^/]+");
        const regex = new RegExp(`^${pattern}$`);
        return regex.test(pathname);
    };
    // Check all routes for isPublicPage meta
    for (const route of routes) {
        // Skip catch-all routes (like 404 handler) to prevent matching everything
        if (route.path.includes("*") || route.path.includes("(.*)")) {
            continue;
        }
        if (route.meta?.isPublicPage) {
            if (matchesRoute(route.path, pathname)) {
                return true;
            }
        }
    }
    return false;
}
// Helper function to check if we should skip auto-authentication (but still initialize Keycloak)
function shouldSkipAutoAuth() {
    const forceLogout = sessionStorage.getItem("force-logout");
    if (!forceLogout)
        return false;
    const logoutTime = parseInt(forceLogout);
    const currentTime = Date.now();
    const timeDiff = currentTime - logoutTime;
    // Skip auto-authentication if logout was recent (within 10 minutes) AND not on login page
    if (timeDiff < 10 * 60 * 1000 && !window.location.pathname.includes("/session-ended")) {
        logger.debug("Recent logout detected, skipping auto-authentication");
        return true;
    }
    // If on login page, clear force logout flag
    if (window.location.pathname.includes("/session-ended")) {
        logger.debug("On login page, clearing force logout flag");
        sessionStorage.removeItem("force-logout");
    }
    // Remove old logout flag if time has passed
    if (timeDiff >= 10 * 60 * 1000) {
        sessionStorage.removeItem("force-logout");
    }
    return false;
}
// Module-level singleton for the auth-bootstrap service. Lazily constructed
// on first access so the keycloak instance and api axios singleton are both
// initialized before the service captures them via closure. Exported for
// downstream consumers (e.g. the 403 recovery interceptor in api.ts).
let authBootstrapInstance = null;
export function getAuthBootstrap() {
    if (authBootstrapInstance === null) {
        authBootstrapInstance = createAuthBootstrapService({
            postBootstrap: async (init) => {
                // BFF response interceptor unwraps ApiResponse<T> for apiType: "bff",
                // so `response.data` is the BootstrapAuthResponseV1 directly. We keep
                // the unwrap fallback for defensive double-shape handling.
                const response = await api.post("/auth/bootstrap", undefined, {
                    apiType: "bff",
                    signal: init?.signal,
                });
                const body = response.data;
                if (body !== null &&
                    typeof body === "object" &&
                    "data" in body &&
                    body.data !== null &&
                    typeof body.data === "object" &&
                    "rightsChanged" in body.data) {
                    return body.data;
                }
                return body;
            },
            keycloak: () => keycloak,
            // opay-front's LoggerService only exposes debug() — route errors through
            // debug() so we still surface them in dev mode without expanding the
            // LoggerService surface in this PR.
            logger: {
                error: (msg, ...args) => logger.debug(msg, ...args),
                debug: (msg, ...args) => logger.debug(msg, ...args),
            },
        });
    }
    return authBootstrapInstance;
}
// Helper function to clear invalid tokens
function clearInvalidTokens() {
    logger.debug("Clearing any invalid or expired tokens from sessionStorage");
    cleanAuthData(); // Use shared cleanup function
    keycloak.token = undefined;
    keycloak.refreshToken = undefined;
}
// Initialize Keycloak
async function initializeKeycloak() {
    try {
        // Always initialize Keycloak instance, but check if we should skip auto-authentication
        const skipAutoAuth = shouldSkipAutoAuth();
        if (skipAutoAuth) {
            logger.debug("Recent logout detected, will skip auto-authentication but still initialize Keycloak");
        }
        logger.debug("Starting Keycloak initialization...");
        // Clear any invalid tokens from previous sessions that might cause 400 errors
        if (!skipAutoAuth) {
            clearInvalidTokens();
        }
        // Validate environment variables first
        const envVars = keycloakConfig;
        logger.debug("📋 Environment variables:", envVars);
        // Check for missing environment variables
        const missingVars = Object.entries(envVars)
            .filter(([, value]) => !value)
            .map(([key]) => key);
        if (missingVars.length > 0) {
            throw new Error(`Missing required environment variables: ${missingVars.join(", ")}. Please check your .env file.`);
        }
        logger.debug("All environment variables are set");
        logger.debug("Keycloak instance config:", keycloakConfig);
        const scope = getScope();
        // Configure init options based on whether we should skip auto-authentication
        const initOptions = {
            onLoad: "check-sso", // Just check SSO status, don't force login
            scope: scope,
            silentCheckSsoRedirectUri: `${location.origin}/silent-check-sso.html`,
            silentCheckSsoFallback: false,
        };
        // Wrap the init call with a timeout to prevent hanging
        logger.debug("Creating Keycloak init promise with config:", initOptions);
        const initPromise = keycloak.init(initOptions).catch((initError) => {
            logger.debug("Keycloak init promise rejected with error:", initError);
            throw initError;
        });
        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error("Keycloak initialization timeout after 15 seconds")), 15000);
        });
        logger.debug("Starting Promise.race between init and timeout...");
        const authenticated = (await Promise.race([initPromise, timeoutPromise]));
        logger.debug("Keycloak initialization completed. Authenticated:", authenticated);
        // Case (b) (OP-5119): silent SSO succeeded but the id_token has no
        // `organization` claim. The cache being set or empty does not matter —
        // an org-less token is functionally invalid for this app. Hand off to
        // the state machine: first hit retries with prompt=login, second hit
        // escalates to a logout + terminal page.
        if (authenticated && !hasOrganizationClaim(keycloak.idTokenParsed)) {
            handleOrgLessSession();
            return;
        }
        authState.isAuthenticated = authenticated;
        authState.isInitialized = true;
        // Start token refresh loop if authenticated
        if (authenticated) {
            // Store token for later use - defensive check for token existence
            if (keycloak.token && keycloak.refreshToken) {
                sessionStorage.setItem("kc-token", keycloak.token);
                sessionStorage.setItem("kc-refresh-token", keycloak.refreshToken);
                // Save user email for login_hint on reconnection
                if (keycloak.tokenParsed?.email) {
                    saveLoginEmail(keycloak.tokenParsed.email);
                }
                // Save organization from token for future logins
                saveOrganizationFromToken();
                clearOrgRecoveryFlag(); // OP-5119: circuit reset on success
                // Initialize session tracking (for 8h inactivity)
                initializeSession();
                // OP-1406: fire-and-forget auth bootstrap so the user's default roles
                // are materialized in Keycloak before downstream API calls. Runs only
                // on the happy path (authenticated + valid organization claim — the
                // OP-5119 org-less recovery branches return earlier above).
                getAuthBootstrap().start();
            }
            else {
                logger.debug("Warning: Authenticated but tokens are missing");
                authState.isAuthenticated = false;
                authState.isInitialized = true;
                return;
            }
            // Start token refresh loop, Check every 60s
            setInterval(async () => {
                try {
                    // Defensive check: verify we still have tokens before attempting refresh
                    if (!keycloak.token || !keycloak.refreshToken) {
                        logger.debug("Tokens missing during refresh interval, treating as logged out");
                        authState.isAuthenticated = false;
                        sessionStorage.removeItem("kc-token");
                        sessionStorage.removeItem("kc-refresh-token");
                        clearSession();
                        return;
                    }
                    // Check session validity (8h inactivity)
                    const sessionState = checkSessionValidity();
                    if (!sessionState.isValid) {
                        logger.debug("Session expired, redirecting to session-ended", {
                            reason: sessionState.reason,
                        });
                        authState.isAuthenticated = false;
                        sessionStorage.removeItem("kc-token");
                        sessionStorage.removeItem("kc-refresh-token");
                        clearSession();
                        keycloak.logout();
                        if (window.location.pathname !== "/session-ended" &&
                            window.location.pathname !== "/error") {
                            window.location.replace("/session-ended");
                        }
                        return;
                    }
                    // Refresh if expiring in 30s
                    const tokenUpdated = await keycloak.updateToken(30);
                    if (tokenUpdated) {
                        logger.debug("Token refreshed successfully");
                        // Update stored tokens
                        if (keycloak.token) {
                            sessionStorage.setItem("kc-token", keycloak.token);
                        }
                        if (keycloak.refreshToken) {
                            sessionStorage.setItem("kc-refresh-token", keycloak.refreshToken);
                        }
                        // Update organization cache from refreshed token
                        saveOrganizationFromToken();
                    }
                }
                catch (error) {
                    logger.debug("Token refresh failed, clearing auth state...", error);
                    // Clear auth state on token refresh failure
                    authState.isAuthenticated = false;
                    sessionStorage.removeItem("kc-token");
                    sessionStorage.removeItem("kc-refresh-token");
                    clearSession();
                    // Defensive: prevent infinite loops by redirecting to logout page
                    if (window.location.pathname !== "/session-ended" &&
                        window.location.pathname !== "/error") {
                        logger.debug("Redirecting to session ended page due to token refresh failure");
                        window.location.replace("/session-ended");
                    }
                }
            }, 60000);
        }
    }
    catch (error) {
        logger.debug("Keycloak initialization failed, error details:", {
            error: error,
            errorType: typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            errorStack: error instanceof Error ? error.stack : undefined,
            isErrorObject: error && typeof error === "object",
            hasErrorProperty: error && typeof error === "object" && "error" in error,
            errorProperty: error && typeof error === "object" && "error" in error ? error.error : undefined,
        });
        // Check if this is a login_required "error" which might be normal behavior
        if (error &&
            typeof error === "object" &&
            "error" in error &&
            error.error === "login_required") {
            logger.debug("Keycloak returned login_required, checking for stored tokens...");
            // Check if we have stored tokens that might still be valid
            const storedToken = sessionStorage.getItem("kc-token");
            const storedRefreshToken = sessionStorage.getItem("kc-refresh-token");
            // Defensive check: both tokens must exist for a valid session
            if (storedToken && storedRefreshToken) {
                logger.debug("Found stored tokens, attempting to validate...");
                try {
                    // Manually set the tokens and try to validate them
                    keycloak.token = storedToken;
                    keycloak.refreshToken = storedRefreshToken;
                    // Try to refresh/validate the token
                    const tokenValid = await keycloak.updateToken(5);
                    if (tokenValid || keycloak.authenticated) {
                        // Case (b) (OP-5119): the stored refresh token is valid but the
                        // id_token's organization claim is missing. Same dispatcher as
                        // the init success path — the cache being set or empty does not
                        // gate the detection.
                        if (!hasOrganizationClaim(keycloak.idTokenParsed)) {
                            handleOrgLessSession();
                            return;
                        }
                        logger.debug("Stored tokens are valid, user is authenticated");
                        authState.isAuthenticated = true;
                        authState.isInitialized = true;
                        // Save user email for login_hint on reconnection
                        if (keycloak.tokenParsed?.email) {
                            saveLoginEmail(keycloak.tokenParsed.email);
                        }
                        // Save organization from token for future logins
                        saveOrganizationFromToken();
                        clearOrgRecoveryFlag(); // OP-5119: circuit reset on success
                        // Initialize session tracking (for 8h inactivity)
                        initializeSession();
                        // Start token refresh loop
                        setInterval(async () => {
                            try {
                                // Check session validity (8h inactivity)
                                const sessionState = checkSessionValidity();
                                if (!sessionState.isValid) {
                                    logger.debug("Session expired in login_required handler, redirecting to session-ended", {
                                        reason: sessionState.reason,
                                    });
                                    authState.isAuthenticated = false;
                                    sessionStorage.removeItem("kc-token");
                                    sessionStorage.removeItem("kc-refresh-token");
                                    clearSession();
                                    if (window.location.pathname !== "/session-ended" &&
                                        window.location.pathname !== "/error") {
                                        window.location.replace("/session-ended");
                                    }
                                    return;
                                }
                                const tokenUpdated = await keycloak.updateToken(30);
                                if (tokenUpdated) {
                                    logger.debug("Token refreshed successfully");
                                    if (keycloak.token) {
                                        sessionStorage.setItem("kc-token", keycloak.token);
                                    }
                                    if (keycloak.refreshToken) {
                                        sessionStorage.setItem("kc-refresh-token", keycloak.refreshToken);
                                    }
                                    // Update organization cache from refreshed token
                                    saveOrganizationFromToken();
                                }
                            }
                            catch (error) {
                                logger.debug("Token refresh failed in loop, clearing auth state...", error);
                                authState.isAuthenticated = false;
                                sessionStorage.removeItem("kc-token");
                                sessionStorage.removeItem("kc-refresh-token");
                                clearSession();
                                // Defensive: prevent infinite loops by redirecting to logout page
                                if (window.location.pathname !== "/session-ended" &&
                                    window.location.pathname !== "/error") {
                                    logger.debug("Redirecting to session ended page due to token refresh failure");
                                    window.location.replace("/session-ended");
                                }
                            }
                        }, 60000);
                        return;
                    }
                    else {
                        logger.debug("Stored tokens are invalid or expired, clearing them");
                        sessionStorage.removeItem("kc-token");
                        sessionStorage.removeItem("kc-refresh-token");
                        // Clear the invalid tokens from Keycloak instance
                        keycloak.token = undefined;
                        keycloak.refreshToken = undefined;
                    }
                }
                catch (tokenError) {
                    logger.debug("Token validation failed, clearing stored tokens:", tokenError);
                    sessionStorage.removeItem("kc-token");
                    sessionStorage.removeItem("kc-refresh-token");
                    // Clear the invalid tokens from Keycloak instance
                    keycloak.token = undefined;
                    keycloak.refreshToken = undefined;
                }
            }
            else if (storedToken || storedRefreshToken) {
                // Defensive: if only one token exists, clear both to avoid inconsistent state
                logger.debug("Inconsistent token state detected, clearing all tokens");
                sessionStorage.removeItem("kc-token");
                sessionStorage.removeItem("kc-refresh-token");
            }
            else {
                logger.debug("No stored tokens found, user needs to login");
            }
            logger.debug("No valid tokens found, user needs to login");
            authState.isAuthenticated = false;
            authState.isInitialized = true;
            return;
        }
        logger.debug("Keycloak initialization failed:", error);
        // Provide more specific error information
        if (error instanceof Error) {
            if (error.message.includes("timeout")) {
                logger.debug("Keycloak server may be unreachable or configuration is incorrect");
            }
            else if (error.message.includes("CORS")) {
                logger.debug("CORS issue - check Keycloak client Web Origins configuration");
            }
            else if (error.message.includes("Invalid")) {
                logger.debug("Invalid configuration - check realm, client ID, and server URL");
            }
        }
        authState.isAuthenticated = false;
        authState.isInitialized = true;
    }
}
export default {
    install: (app) => {
        // Provide Keycloak instance globally immediately
        app.provide(KEYCLOAK_KEY, authState);
        // Skip Keycloak initialization completely for public pages (based on route metadata)
        // Exception: /comfirm/:organizationId needs Keycloak initialized for login flow
        const pathname = window.location.pathname;
        const isConfirmRoute = pathname.startsWith("/comfirm/");
        if (isPublicPage(pathname) && !isConfirmRoute) {
            logger.debug(`Skipping Keycloak initialization for public page: ${pathname}`);
            authState.isInitialized = true; // Mark as initialized
            authState.isAuthenticated = false; // Set as not authenticated
            return; // Exit early - don't initialize Keycloak
        }
        // Initialize Keycloak asynchronously without blocking the app for all other pages
        // This includes the confirm route which needs Keycloak for authentication
        initializeKeycloak().catch((error) => {
            logger.debug("Failed to initialize Keycloak:", error);
        });
    },
};
// Helper function to access Keycloak
export function useKeycloak() {
    const auth = inject(KEYCLOAK_KEY);
    if (!auth) {
        throw new Error("Keycloak instance not found. Make sure the plugin is registered in main.ts.");
    }
    return auth;
}
export { authState, keycloak };
